import React, { useEffect, useMemo, useState } from "react";

import { CompanyFilterTypes, DateRangeOptionsOld, InvoiceType } from "@assets/constants";
import { useAppDispatch, useAppSelector } from "@assets/hooks";
import { TableInfo, TransactionFilter, UserProfileViewModel } from "@assets/types";
import { ArrayUtilities, StringUtilities } from "@assets/utilities";
import Alert from "@components/Alert/alert";
import Button from "@components/Button/button";
import IconButton from "@components/Button/iconButton";
import Card from "@components/Card/card";
import Icon from "@components/CustomIcon/Icons";
import Divider from "@components/Divider/divider";
import ExpandableComponent from "@components/ExpendableComponent/expendableComponent";
import Group from "@components/Group/group";
import Checkbox from "@components/Input/checkbox";
import stylesDatePicker from "@components/Input/reactDateOverrides.module.less";
import LabelEx from "@components/Label/labelEx";
import ListFilterLayout from "@components/Layout/listFilterLayout";
import Link from "@components/Link/link";
import Pagination from "@components/Pagination/pagination";
import SearchEx from "@components/Search/searchEx";
import Select from "@components/Select/select";
import Table from "@components/Table/table";
import Tag from "@components/Tag/tag";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import stylesShipments from "@pages/Shipments/shipments.module.less";
import {
  customerState,
  getCustomerStatus,
  getCustomerStatusByDate,
} from "@state/slices/customerSlice";
import {
  filterTransactions,
  filterTransactionsExcel,
  filterTransactionsState,
} from "@state/slices/transactionsSlice";
import { getUserProfileFromAPI, userState } from "@state/slices/userSlice";
import { format, subMonths } from "date-fns";
import { toUpper } from "lodash";
import moment from "moment";
import { DateRangePicker, FocusedInputShape, isInclusivelyBeforeDay } from "react-dates";
import { useTranslation } from "react-i18next";
import { FaBan, FaInfoCircle, FaSpinner } from "react-icons/fa";
import { FiX } from "react-icons/fi";
import "react-toastify/dist/ReactToastify.css";

interface FinanceProps {
  path?: string;
}

const Finance: React.FC<FinanceProps> = () => {
  const { t } = useTranslation();
  const PageSize = 10;
  const dispatch = useAppDispatch();

  const userProfile = useAppSelector(userState).userProfile;
  const customerStatus = useAppSelector(customerState).customerStatus;
  const customerStatusByDate = useAppSelector(customerState).customerStatusByDate;
  const filteredTransactions = useAppSelector(filterTransactionsState).filterTransactions;
  const loadingCustomerStatus = useAppSelector(customerState).loadingCustomerStatus;
  const loadingCustomerStatusByDate = useAppSelector(customerState).loadingCustomerStatusByDate;
  const loadingFilteredTransactions =
    useAppSelector(filterTransactionsState).loadingFilteredTransactions;

  let initialFilterInvoicesState: TransactionFilter = {
    SapIDs: [userProfile?.SelectedCompany?.SapID ?? ""],
    InvoiceTypes: [],
    SearchString: "",
    DateFrom: subMonths(new Date(), 1),
    DateTo: new Date(),
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [currentPage, setCurrentPage] = useState<number | string | any>(1);
  const [headerFilter, setHeaderFilter] = useState<string>("");
  const [sortType, setSortType] = useState(false);

  const [companyFilter, setCompanyFilter] = useState<CompanyFilterTypes>(
    CompanyFilterTypes.SelectedCompany
  );
  const [isCustomsClearance, setIsCustomsClearance] = useState<boolean>(false);
  const [isStorage, setIsStorage] = useState<boolean>(false);
  const [isTransport, setIsTransport] = useState<boolean>(false);
  const [invoicesTypes, setInvoicesTypes] = useState<string[]>(
    initialFilterInvoicesState.InvoiceTypes
  );
  const [invoiceNumber, setInvoiceNumber] = useState<string>(
    initialFilterInvoicesState.SearchString
  );
  const [sapIds, setSapIds] = useState<string[]>(initialFilterInvoicesState.SapIDs);
  const [startDate, setStartDate] = useState<Date | null>(initialFilterInvoicesState.DateFrom);
  const [endDate, setEndDate] = useState<Date | null>(initialFilterInvoicesState.DateTo);
  const [mobileFilter, setMobileFilter] = useState<boolean>(false);

  const [selectedDateRangeOption, setSelectedDateRangeOption] = useState<string | null>(null);
  const [focusedInput, setFocusedInput] = useState<FocusedInputShape | null>(null);

  const resetStatus = (user: UserProfileViewModel | null) => {
    setCurrentPage(1);
    setHeaderFilter("");
    setCompanyFilter(CompanyFilterTypes.SelectedCompany);
    setIsCustomsClearance(false);
    setIsStorage(false);
    setIsTransport(false);
    setInvoicesTypes(initialFilterInvoicesState.InvoiceTypes);
    setInvoiceNumber(initialFilterInvoicesState.SearchString);
    setStartDate(initialFilterInvoicesState.DateFrom);
    setEndDate(initialFilterInvoicesState.DateTo);
    setSapIds(initialFilterInvoicesState.SapIDs);
    setSelectedDateRangeOption(DateRangeOptionsOld.CUSTOM_DATE);
    setFocusedInput(null);
    setSortType(false);

    dispatch(getCustomerStatus(user?.SelectedCompany.SapID ?? ""));
    dispatch(
      getCustomerStatusByDate({
        sapId: user?.SelectedCompany.SapID ?? "",
        dateFrom: initialFilterInvoicesState.DateFrom,
        dateTo: initialFilterInvoicesState.DateTo,
      })
    );
    dispatch(filterTransactions(initialFilterInvoicesState));
  };

  useEffect(() => {
    Promise.all([dispatch(getUserProfileFromAPI()).unwrap()]).then(([user]) => {
      initialFilterInvoicesState = {
        SapIDs: [user?.SelectedCompany?.SapID ?? ""],
        InvoiceTypes: [],
        SearchString: "",
        DateFrom: subMonths(new Date(), 1),
        DateTo: new Date(),
      };
      resetStatus(user);
    });
  }, []);

  const dateRangeOptions = [
    {
      value: DateRangeOptionsOld.LAST_WEEK,
      translationId: `LABEL_${DateRangeOptionsOld.LAST_WEEK}`,
      text: "",
    },
    {
      value: DateRangeOptionsOld.LAST_TWO_WEEKS,
      translationId: `LABEL_${DateRangeOptionsOld.LAST_TWO_WEEKS}`,
      text: "",
    },
    {
      value: DateRangeOptionsOld.LAST_MONTH,
      translationId: `LABEL_${DateRangeOptionsOld.LAST_MONTH}`,
      text: "",
    },
    {
      value: DateRangeOptionsOld.LAST_THREE_MONTHS,
      translationId: `LABEL_${DateRangeOptionsOld.LAST_THREE_MONTHS}`,
      text: "",
    },
    {
      value: DateRangeOptionsOld.LAST_YEAR,
      translationId: `LABEL_${DateRangeOptionsOld.LAST_YEAR}`,
      text: "",
    },
    {
      value: DateRangeOptionsOld.CUSTOM_DATE,
      translationId: `LABEL_${DateRangeOptionsOld.CUSTOM_DATE}`,
      text: "",
    },
  ];

  const currentTableData = useMemo(() => {
    if (filteredTransactions) {
      const firstPageIndex = (currentPage - 1) * PageSize;
      const lastPageIndex = firstPageIndex + PageSize;

      let invoiceData = [...filteredTransactions];

      if (sortType) {
        invoiceData = ArrayUtilities.sortAlphabeticallyAscending(
          headerFilter,
          filteredTransactions.slice()
        );
      } else {
        invoiceData = ArrayUtilities.sortAlphabeticallyDescending(
          headerFilter,
          filteredTransactions.slice()
        );
      }

      return invoiceData.slice(firstPageIndex, lastPageIndex);
    }
    return [];
  }, [currentPage, headerFilter, sortType, loadingFilteredTransactions]);

  const onSortTable = (head: string) => {
    let tableHead = "";
    switch (head) {
      case t("LABEL_INVOICE"):
        tableHead = "InvoiceNumberField";
        break;
      case t("LABEL_TYPE"):
        tableHead = "InvoiceTypeLabel";
        break;
      case t("LABEL_DATE"):
        tableHead = "DueDateField";
        break;
      case t("LABEL_AMOUNT"):
        tableHead = "AmountField";
        break;
      case t("LABEL_DOCUMENT_NUMBER"):
        tableHead = "SapDocumentNumberField";
        break;
      case t("LABEL_COMPANY"):
        tableHead = "CompanyCodeField";
        break;
      case t("LABEL_STATUS"):
        tableHead = "Status";
        break;
    }
    setHeaderFilter(tableHead);
    setSortType(!sortType);
  };

  const onChangeCompanyStatus = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCompanyFilter(
      e.target.value === CompanyFilterTypes.SelectedCompany
        ? CompanyFilterTypes.SelectedCompany
        : CompanyFilterTypes.AllCompanies
    );
    const sapIds: string[] = [];
    if (e.target.value === CompanyFilterTypes.SelectedCompany) {
      sapIds.push(userProfile?.SelectedCompany?.SapID ?? "");
    } else {
      userProfile?.Companies.forEach((company) => {
        sapIds.push(company.SapID);
      });
    }

    setSapIds(sapIds);
    dispatch(
      filterTransactions({
        SapIDs: sapIds,
        InvoiceTypes: invoicesTypes,
        SearchString: invoiceNumber,
        DateFrom: startDate ?? subMonths(new Date(), 1),
        DateTo: endDate ?? new Date(),
      })
    );
  };

  const handleDateChange = (
    dateStart: moment.Moment | null,
    dateEnd: moment.Moment | null,
    shouldRender = true,
    revertToCustom = false
  ) => {
    if (revertToCustom) {
      setSelectedDateRangeOption(DateRangeOptionsOld.CUSTOM_DATE);
    }
    const start = dateStart?.toDate() ?? null;
    const end = dateEnd?.toDate() ?? null;
    if (
      (dateEnd && dateEnd.format("DD, MM, YYYY") != moment(endDate).format("DD, MM, YYYY")) ||
      shouldRender
    ) {
      if (start && end) {
        dispatch(
          getCustomerStatusByDate({
            sapId: userProfile?.SelectedCompany.SapID ?? "",
            dateFrom: start,
            dateTo: end,
          })
        );
        dispatch(
          filterTransactions({
            SapIDs: sapIds,
            InvoiceTypes: invoicesTypes,
            SearchString: invoiceNumber,
            DateFrom: start,
            DateTo: end,
          })
        );
      }
    }

    setStartDate(start);
    setEndDate(end);
  };

  const renderYearOptions = () => {
    const optionsArray = [];
    for (let x = 0; x < 5; x++) {
      optionsArray[x] = <option value={moment().year() - x + 1}>{moment().year() - x + 1}</option>;
    }
    return optionsArray;
  };

  const handleDateRangeSelect = (value: string): string | null => {
    switch (value) {
      case DateRangeOptionsOld.INIT: {
        setSelectedDateRangeOption(DateRangeOptionsOld.INIT);
        handleDateChange(moment().subtract(4, "weeks"), moment());
        return selectedDateRangeOption;
      }
      case DateRangeOptionsOld.LAST_WEEK: {
        setSelectedDateRangeOption(DateRangeOptionsOld.LAST_WEEK);
        handleDateChange(moment().subtract(1, "weeks"), moment());
        return selectedDateRangeOption;
      }
      case DateRangeOptionsOld.LAST_TWO_WEEKS: {
        setSelectedDateRangeOption(DateRangeOptionsOld.LAST_TWO_WEEKS);
        handleDateChange(moment().subtract(2, "weeks"), moment());
        return selectedDateRangeOption;
      }
      case DateRangeOptionsOld.LAST_MONTH: {
        setSelectedDateRangeOption(DateRangeOptionsOld.LAST_MONTH);
        handleDateChange(moment().subtract(1, "months"), moment());
        return selectedDateRangeOption;
      }
      case DateRangeOptionsOld.LAST_THREE_MONTHS: {
        setSelectedDateRangeOption(DateRangeOptionsOld.LAST_THREE_MONTHS);
        handleDateChange(moment().subtract(3, "months"), moment());
        return selectedDateRangeOption;
      }
      case DateRangeOptionsOld.LAST_YEAR: {
        setSelectedDateRangeOption(DateRangeOptionsOld.LAST_YEAR);
        handleDateChange(moment().subtract(1, "years"), moment());
        return selectedDateRangeOption;
      }
      case DateRangeOptionsOld.CUSTOM_DATE: {
        setSelectedDateRangeOption(DateRangeOptionsOld.CUSTOM_DATE);
        return selectedDateRangeOption;
      }
      default:
        return null;
    }
  };

  const handleAddInvoiceType = (invoiceType: string): string[] => {
    let newInvoicesTypes: string[] = [];
    newInvoicesTypes = [...invoicesTypes, invoiceType];
    setInvoicesTypes(newInvoicesTypes);
    return newInvoicesTypes;
  };

  const handleRemoveInvoiceType = (invoiceType: string): string[] => {
    const newInvoicesTypes = [...invoicesTypes];
    newInvoicesTypes.splice(invoicesTypes.indexOf(invoiceType), 1);
    setInvoicesTypes(newInvoicesTypes);
    return newInvoicesTypes;
  };

  const onChangeCustomsClearance = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newInvoicesType: string[] = [];
    setIsCustomsClearance((oldVal) => !oldVal);
    if (e.target.checked) {
      newInvoicesType = handleAddInvoiceType(InvoiceType.Customs);
    } else {
      newInvoicesType = handleRemoveInvoiceType(InvoiceType.Customs);
    }
    dispatch(
      filterTransactions({
        SapIDs: sapIds,
        InvoiceTypes: newInvoicesType,
        SearchString: invoiceNumber,
        DateFrom: startDate ?? subMonths(new Date(), 1),
        DateTo: endDate ?? new Date(),
      })
    );
  };

  const onChangeStorage = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newInvoicesType: string[] = [];
    setIsStorage((oldVal) => !oldVal);
    if (e.target.checked) {
      newInvoicesType = handleAddInvoiceType(InvoiceType.Storage);
    } else {
      newInvoicesType = handleRemoveInvoiceType(InvoiceType.Storage);
    }
    dispatch(
      filterTransactions({
        SapIDs: sapIds,
        InvoiceTypes: newInvoicesType,
        SearchString: invoiceNumber,
        DateFrom: startDate ?? subMonths(new Date(), 1),
        DateTo: endDate ?? new Date(),
      })
    );
  };

  const onChangeTransport = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newInvoicesType: string[] = [];
    setIsTransport((oldVal) => !oldVal);
    if (e.target.checked) {
      newInvoicesType = handleAddInvoiceType(InvoiceType.Transport);
    } else {
      newInvoicesType = handleRemoveInvoiceType(InvoiceType.Transport);
    }
    dispatch(
      filterTransactions({
        SapIDs: sapIds,
        InvoiceTypes: newInvoicesType,
        SearchString: invoiceNumber,
        DateFrom: startDate ?? subMonths(new Date(), 1),
        DateTo: endDate ?? new Date(),
      })
    );
  };

  const onChangeInvoiceNumber = (value: string) => {
    setInvoiceNumber(value);
    if (value.length > 5) {
      dispatch(
        filterTransactions({
          SapIDs: sapIds,
          InvoiceTypes: invoicesTypes,
          SearchString: value,
          DateFrom: startDate ?? subMonths(new Date(), 1),
          DateTo: endDate ?? new Date(),
        })
      );
    }
  };

  // TODO: Implement this when open multiple invoices is ready
  // const onHandleCheckBox = (checked: boolean, index: number) => {
  //   // insert here implementation for table checkbox
  // };

  const getInvoiceStatusTag = (status: string) => {
    if (status === "Deposit") {
      return (
        <Tag color="success" isFaded size="small">
          {t("LABEL_DEPOSIT")}
        </Tag>
      );
    }
    if (status === "Unpaid") {
      return (
        <Tag color="warning" isFaded size="small">
          {t("LABEL_UNPAID")}
        </Tag>
      );
    }
    if (status === "Paid") {
      return (
        <Tag color="success" isFaded size="small">
          {t("LABEL_PAID")}
        </Tag>
      );
    }
    if (status === "Credit") {
      return (
        <Tag color="primary" size="small">
          {t("LABEL_CREDIT")}
        </Tag>
      );
    }
    return;
  };

  const invoiceDataTable: TableInfo = {
    tableHeader: [
      // TODO: Implement this when open multiple invoices is ready
      // <Checkbox type="checkbox" value="" onChange={() => {}} />,
      t("LABEL_STATUS"),
      t("LABEL_INVOICE"),
      companyFilter === CompanyFilterTypes.AllCompanies ? t("LABEL_COMPANY") : undefined,
      t("LABEL_TYPE"),
      t("LABEL_DATE"),
      t("LABEL_AMOUNT"),
      t("LABEL_DOCUMENT_NUMBER"),
    ],
    tableData:
      currentTableData.length > 0
        ? currentTableData.map((invoiceData) => {
            return [
              // TODO: Implement this when open multiple invoices is ready
              // <Checkbox
              //   type="checkbox"
              //   value=""
              //   onChange={(event) =>
              //     onHandleCheckBox(event.target.checked, index)
              //   }
              // />,
              getInvoiceStatusTag(invoiceData.Status ?? ""),
              <Link
                className="text-jonar-blue"
                href={invoiceData.InvoiceUrl ?? ""}
                key={`view_invoice-${invoiceData.InvoiceNumberField}`}
                target="_blank"
              >
                {t(invoiceData?.InvoiceNumberField ?? "")}
              </Link>,
              companyFilter === CompanyFilterTypes.AllCompanies
                ? invoiceData?.CompanyCodeField
                : null,
              t(`${invoiceData?.InvoiceTypeLabel}`),
              invoiceData?.DueDateField
                ? format(new Date(invoiceData?.DueDateField), "dd.MM.yyyy")
                : "",
              StringUtilities.numberCommaDecimal(parseInt(invoiceData?.AmountField ?? "")),
              invoiceData?.SapDocumentNumberField,
            ];
          })
        : [],
    classes: ["", "", companyFilter === CompanyFilterTypes.AllCompanies ? "" : "hidden"],
  };

  return (
    <main className="overflow-hidden static h-auto w-full mx-auto flex content-center flex-grow flex-col">
      <ListFilterLayout>
        <>
          <div className="listFilterLayout__content relative w-auto lg:w-full flex-1 p-6 h-auto overflow-auto">
            <div className="listFilterLayout__content__header flex justify-between md:block mr-5">
              <div className="listFilterLayout__content__header__upper flex justify-between">
                <div>
                  <Icon className="inline-block h-8 w-auto mr-2 stroke-current" name="jonarArrow" />
                  <h1 className="inline-block text-1xl font-bold text-jonar-blue uppercase leading-7 align-bottom">
                    {t("LABEL_FINANCE")}
                  </h1>
                </div>
              </div>
              <Divider className="mt-6" />
              <div className="listFilterLayout__content__header__lower pr-3 lg:pr-0 cursor-pointer flex justify-between items-center mb-1 py-2" />
            </div>
            <div className="mr-6">
              {companyFilter === CompanyFilterTypes.SelectedCompany && (
                <>
                  {customerStatus && customerStatus[0]?.Blocked && (
                    <Alert
                      className="py-2 px-2 mb-6 text-base flex items-start w-full"
                      color="error"
                    >
                      <FaBan className="absolut text-error mr-2 mt-1 w-10" />
                      {t("TEXT_ACCOUNT_CLOSED")}
                    </Alert>
                  )}
                  <ExpandableComponent
                    className="ml-8"
                    closedTitle={t("LABEL_SHOW")}
                    expandedTitle={t("LABEL_HIDE")}
                    fixedComponent={t("LABEL_FINANCIAL_STATUS")}
                    isExpanded
                    position="right"
                  >
                    <div className="flex gap-8 mt-8 mb-4">
                      <Card className="px-6 py-4 gap-1 box-border flex flex-col items-start min-w-232 h-28 bg-neutral-100 rounded border-neutral-100">
                        {loadingCustomerStatus ||
                        loadingCustomerStatusByDate ||
                        loadingFilteredTransactions ? (
                          <FaSpinner className="fa-spin flex w-full h-1/3 mt-auto mb-auto" />
                        ) : (
                          <>
                            <LabelEx
                              className="text-3.25 font-semibold"
                              labelCase="upper"
                              labelColor="neutral-800"
                            >
                              {t("LABEL_TOTAL_BALANCE")}
                            </LabelEx>
                            <div className="text-neutral-900 font-semibold text-lg">
                              {`${
                                customerStatus && customerStatus[0]?.TotalBalance
                                  ? t(
                                      StringUtilities.numberCommaDecimal(
                                        customerStatus[0].TotalBalance,
                                        0
                                      )
                                    )
                                  : ""
                              }
                            ${
                              customerStatus && customerStatus[0]?.Currency
                                ? t(customerStatus[0]?.Currency)
                                : ""
                            }`}
                            </div>
                          </>
                        )}
                      </Card>
                      <Card className="px-6 py-4 gap-1 box-border flex flex-col items-start min-w-232 h-28 bg-neutral-100 rounded border-neutral-100">
                        {loadingCustomerStatus ||
                        loadingCustomerStatusByDate ||
                        loadingFilteredTransactions ? (
                          <FaSpinner className="fa-spin flex w-full h-1/3 mt-auto mb-auto" />
                        ) : (
                          <>
                            <LabelEx
                              className="text-3.25 font-semibold"
                              labelCase="upper"
                              labelColor="neutral-800"
                            >
                              {t("LABEL_MAXIMUM_WITHDRAWAL")}
                            </LabelEx>
                            <div className="text-neutral-900 font-semibold text-lg">
                              {`${
                                customerStatus && customerStatus[0]?.MaximumWithdrawal
                                  ? t(
                                      StringUtilities.numberCommaDecimal(
                                        customerStatus[0].MaximumWithdrawal,
                                        0
                                      )
                                    )
                                  : ""
                              } ${
                                customerStatus && customerStatus[0]?.Currency
                                  ? t(customerStatus[0]?.Currency)
                                  : ""
                              }`}
                            </div>
                          </>
                        )}
                      </Card>
                      <Card className="px-6 py-4 gap-1 box-border flex flex-col items-start min-w-232 h-28 bg-neutral-100 rounded border-neutral-100 mr-6">
                        {loadingCustomerStatus ||
                        loadingCustomerStatusByDate ||
                        loadingFilteredTransactions ? (
                          <FaSpinner className="fa-spin flex w-full h-1/3 mt-auto mb-auto" />
                        ) : (
                          <>
                            <LabelEx
                              className="text-3.25 font-semibold"
                              labelCase="upper"
                              labelColor="neutral-800"
                            >
                              {t("LABEL_BALANCE_OVER_SELECTED_PERIOD")}
                            </LabelEx>
                            {customerStatusByDate &&
                            customerStatusByDate[0]?.KeyBalanceFrom &&
                            customerStatusByDate[0]?.KeyBalanceFrom?.Balance &&
                            customerStatusByDate[0]?.KeyBalanceTo &&
                            customerStatusByDate[0]?.KeyBalanceTo?.Balance ? (
                              <div className="text-neutral-900 font-normal text-sm">
                                <div>
                                  {`${t("TEXT_START")}
                              ${" ("}
                              ${startDate && format(startDate, "dd.MM.yyyy")}
                              ${"):"}
                              ${t(
                                StringUtilities.numberCommaDecimal(
                                  customerStatusByDate[0].KeyBalanceFrom.Balance,
                                  0
                                )
                              )}
                              ${
                                customerStatusByDate[0]?.KeyBalanceFrom?.Currency
                                  ? t(customerStatusByDate[0].KeyBalanceFrom?.Currency)
                                  : ""
                              }`}
                                </div>
                                <div>
                                  {`${t("TEXT_END")}
                              ${"("}
                              ${endDate && format(endDate, "dd.MM.yyyy")}
                              ${"):"}
                              ${t(
                                StringUtilities.numberCommaDecimal(
                                  customerStatusByDate[0].KeyBalanceTo.Balance,
                                  0
                                )
                              )}
                              ${
                                customerStatusByDate[0]?.KeyBalanceTo?.Currency
                                  ? t(customerStatusByDate[0].KeyBalanceTo?.Currency)
                                  : ""
                              }`}
                                </div>
                              </div>
                            ) : (
                              <div className="text-neutral-900 font-semibold text-lg">
                                {t("TEXT_NOT_AVAILABLE")}
                              </div>
                            )}
                          </>
                        )}
                      </Card>
                    </div>
                    <div className="flex gap-2 mb-8">
                      <LabelEx className="font-semibold text-sm" labelColor="neutral-800">
                        {t("LABEL_PAYMENT_TERMS") + ":"}
                      </LabelEx>
                      <span className="text-neutral-900">
                        {customerStatus && customerStatus[0]?.CashPayment
                          ? t("TEXT_PREPAYMENTS")
                          : t("TEXT_ON_CREDIT")}
                      </span>
                    </div>
                    <Divider className="mr-6" />
                  </ExpandableComponent>
                </>
              )}

              {companyFilter === CompanyFilterTypes.AllCompanies && (
                <Alert className="py-2 px-2 mb-6 text-base flex items-start w-full">
                  <FaInfoCircle className="absolut text-info mr-2 mt-1 w-10" />
                  {t("TEXT_FINANCE_STATUS")}
                </Alert>
              )}

              <div className="mt-8 flex justify-between">
                <SearchEx
                  className="mb-4"
                  id="invoiceNumber"
                  isClearable={!!invoiceNumber}
                  onChange={(e) => onChangeInvoiceNumber(e.target.value)}
                  onClear={() => onChangeInvoiceNumber(initialFilterInvoicesState.SearchString)}
                  placeholder={t("PLACEHOLDER_SEARCH_INVOICE_NUMBER")}
                  value={invoiceNumber}
                />
                <Button
                  className="flex rounded float-right mr-6"
                  onClick={() => {
                    dispatch(
                      filterTransactionsExcel({
                        SapIDs: sapIds,
                        InvoiceTypes: invoicesTypes,
                        SearchString: invoiceNumber,
                        DateFrom: startDate ?? subMonths(new Date(), 1),
                        DateTo: endDate ?? new Date(),
                      })
                    );
                  }}
                >
                  {t("LABEL_EXCEL_EXPORT")}
                </Button>
              </div>
              <div className="mb-6 text-neutral-600">
                {`${
                  filteredTransactions
                    ? `${t("TEXT_N_INVOICES_FOUND", {
                        count: filteredTransactions?.length,
                      })}`
                    : `${t("TEXT_NO_INVOICES_FOUND")}`
                }`}
              </div>
              {filteredTransactions && filteredTransactions?.length > 0 && (
                <>
                  <div className="py-8 pr-6">
                    <Table
                      className="overflow-auto block whitespace-nowrap table-auto"
                      isBordered
                      isStriped
                      onClickHeader={(head) => onSortTable(head)}
                      shouldFade
                      tableInfo={invoiceDataTable}
                    />
                  </div>
                  <Pagination
                    className="w-auto flex"
                    currentPage={currentPage}
                    onPageChange={(page) => setCurrentPage(page)}
                    pageSize={PageSize}
                    totalCount={filteredTransactions?.length ?? 0}
                  />
                </>
              )}
            </div>
          </div>
          <div
            className={`listFilterLayout__filter lg:flex-initial bg-lighter-gray p-5 absolute xl:relative z-20 right-0 hidden xl:block ${
              mobileFilter ? "showFilter animate-fade-in min-h-full h-auto xl:h-auto" : ""
            }`}
            id="dpFilterPanel"
            onClick={() => setMobileFilter(false)}
          >
            <button
              className="absolute transition duration-300 top-4r inline-block xl:hidden right-5r bg-lighter-gray border border-current text-jonar-blue hover:bg-jonar-blue hover:text-white p-1"
              onClick={() => setMobileFilter(false)}
              type="button"
            >
              <FiX className="text-xl" />
            </button>
            {userProfile && userProfile?.Companies?.length > 0 && (
              <>
                <Group direction="col" title={t("LABEL_FINANCIAL_INFORMATION")}>
                  <Checkbox
                    checked={companyFilter === CompanyFilterTypes.SelectedCompany}
                    className="pb-1"
                    label={t("LABEL_CHOSEN_COMPANY")}
                    name="company"
                    onChange={onChangeCompanyStatus}
                    type="radio"
                    value={CompanyFilterTypes.SelectedCompany}
                  />
                  <Checkbox
                    checked={companyFilter === CompanyFilterTypes.AllCompanies}
                    className="pb-1"
                    label={t("LABEL_MY_COMPANIES")}
                    name="company"
                    onChange={onChangeCompanyStatus}
                    type="radio"
                    value={CompanyFilterTypes.AllCompanies}
                  />
                </Group>
                <Divider className="mt-6 pt-6" />
              </>
            )}

            <Group
              actions={
                <IconButton
                  active={false}
                  className="absolute right-2"
                  onClick={() => resetStatus(userProfile)}
                >
                  <FontAwesomeIcon icon={faTimes} />
                  <span className="pl-2 font-semibold">{t("LABEL_CLEAR_FILTERS")}</span>
                </IconButton>
              }
              direction="col"
              title={t("LABEL_FILTER_SEARCH")}
            >
              <LabelEx
                className="text-sm font-semibold mt-2"
                labelCase="upper"
                labelColor="neutral-800"
              >
                {t("LABEL_DATE_RANGE")}
              </LabelEx>
              <div className="datePickerContainerFilter flex border border-base rounded h-30px">
                <div
                  className={`w-3/5 bg-white border-transparent focus:border-transparent focus:ring-0 ${stylesDatePicker.dateRangePickerOver}`}
                >
                  <DateRangePicker
                    displayFormat="DD.MM.YYYY"
                    endDate={moment(endDate)}
                    endDateId="timePeriodEndDate"
                    focusedInput={focusedInput}
                    isOutsideRange={(day) => !isInclusivelyBeforeDay(day, moment().add(1, "years"))}
                    numberOfMonths={1}
                    onDatesChange={({ startDate, endDate }) =>
                      handleDateChange(startDate, endDate, false, true)
                    }
                    onFocusChange={(focusedInput) => setFocusedInput(focusedInput)}
                    renderMonthElement={({ month, onMonthSelect, onYearSelect }) => (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        <div>
                          <select
                            className="border-transparent focus:border-transparent focus:ring-0"
                            onChange={(e) => {
                              onMonthSelect(month, e.target.value);
                            }}
                            value={month.month()}
                          >
                            {moment.months().map((label, value) => (
                              <option key={value} value={value}>
                                {t("LABEL_MONTH_" + toUpper(label))}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div style={{ marginLeft: "10px" }}>
                          <select
                            className="border-transparent focus:border-transparent focus:ring-0"
                            onChange={(e) => {
                              onYearSelect(month, e.target.value);
                            }}
                            value={month.year()}
                          >
                            {renderYearOptions()}
                          </select>
                        </div>
                      </div>
                    )}
                    startDate={moment(startDate)}
                    startDateId="timePeriodStartDate"
                  />
                </div>
                <div className="w-2/5">
                  <Select
                    className={`w-full text-sm rounded-none date-range-selector ${stylesShipments.dateRangeSelector}`}
                    name="sortBy"
                    onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
                      handleDateRangeSelect(event.target.value)
                    }
                    options={dateRangeOptions}
                    sortDirection={false}
                    value={selectedDateRangeOption ?? ""}
                  />
                </div>
              </div>
            </Group>
            <Divider className="mt-6 pt-6" />
            <Group direction="col" title={t("LABEL_INVOICE_TYPE")}>
              <Checkbox
                checked={isCustomsClearance}
                className="pb-1"
                label={t("LABEL_CUSTOMS_CLEARANCE")}
                onChange={onChangeCustomsClearance}
                type="checkbox"
              />
              <Checkbox
                checked={isStorage}
                className="pb-1"
                label={t("LABEL_STORAGE")}
                onChange={onChangeStorage}
                type="checkbox"
              />
              <Checkbox
                checked={isTransport}
                className="pb-1"
                label={t("LABEL_TRANSPORT")}
                onChange={onChangeTransport}
                type="checkbox"
              />
            </Group>
          </div>
          <div className="fixed md:absolute z-40 bottom-0 left-0 md:left-auto md:bottom-auto md:top-0 md:right-0 w-full md:w-auto listFilterLayout__filter-btn block xl:hidden md:px-5 md:py-3 leading-none">
            <button
              className="px-4 py-5 md:pt-3 pb-4 md:pb-2 w-full md:w-auto bg-lighter-gray uppercase focus:outline-none leading-none"
              onClick={() => setMobileFilter(!mobileFilter)}
              type="button"
            >
              <span className="font-bold">
                {!mobileFilter ? t("LABEL_FILTER_SEARCH") : t("LABEL_HIDE")}
              </span>
            </button>
          </div>
        </>
      </ListFilterLayout>
    </main>
  );
};

export default Finance;
